import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import { Modal } from 'src/components/modals/comps';
import Input, { Textarea } from 'src/components/form/Input';
import Select from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { showSuccessToast } from 'src/components/Toast';
import { useAuth } from 'src/state/auth';
import {
  UPDATE_FRAMEWORK_METADATA,
  UpdateFrameworkMetadataMutation,
  UpdateFrameworkMetadataMutationVariables,
} from 'src/graphql/contentImport';

const StyledModal = styled(Modal)`
  width: 760px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const FormInput = styled(Input)`
  &[type='text'] {
    padding: 0 18px;
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 118px;
  padding: 14px 18px;
`;

const FormSelect = styled(Select)`
  width: 100%;
  min-width: 0;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface AuthorOption {
  value: string;
  label: string;
  profilePhotoPath: string | null;
}

interface FrameworkOption {
  value: string;
  label: string;
}

export interface ControlFrameworkSummaryOption {
  id: string;
  name: string;
  shortname?: string | null;
  author?: string | null;
}

interface ControlFrameworkMetadataQuery {
  controlFrameworks: Array<{
    id: string;
    name: string;
    shortname?: string | null;
    author?: string | null;
    description?: string | null;
  }>;
}

interface ControlFrameworkMetadataQueryVariables {
  id: string;
}

const CONTROL_FRAMEWORK_METADATA = gql`
  query controlFrameworkMetadata($id: UxId) {
    controlFrameworks(id: $id) {
      id
      name
      shortname
      author
      description
    }
  }
`;

interface EditControlFrameworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdited: () => Promise<void> | void;
  frameworks: ControlFrameworkSummaryOption[];
}

export const EditControlFrameworkModal: React.FC<EditControlFrameworkModalProps> = ({
  isOpen,
  onClose,
  onEdited,
  frameworks,
}) => {
  const { usersOptios } = useAuth();

  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [frameworkName, setFrameworkName] = useState('');
  const [shortName, setShortName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState<AuthorOption | null>(null);
  const [error, setError] = useState('');

  const [runUpdateMetadata, { loading: isSaving }] = useMutation<
    UpdateFrameworkMetadataMutation,
    UpdateFrameworkMetadataMutationVariables
  >(UPDATE_FRAMEWORK_METADATA);

  const { data: metadataData, loading: isMetadataLoading } = useQuery<
    ControlFrameworkMetadataQuery,
    ControlFrameworkMetadataQueryVariables
  >(CONTROL_FRAMEWORK_METADATA, {
    variables: {
      id: selectedFrameworkId,
    },
    skip: !isOpen || !selectedFrameworkId,
    fetchPolicy: 'network-only',
  });

  const authorOptions = useMemo<AuthorOption[]>(() => {
    const seen = new Set<string>();
    const options: AuthorOption[] = [];

    for (const owner of usersOptios || []) {
      const label = owner.text || owner.email || '';
      if (!label) continue;

      const key = `${owner.id || ''}::${label.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);

      options.push({
        value: owner.id || owner.email || owner.text || label,
        label,
        profilePhotoPath: owner.profilePhotoPath || null,
      });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [usersOptios]);

  const frameworkOptions = useMemo<FrameworkOption[]>(() => {
    return (frameworks || [])
      .map((framework) => ({
        value: framework.id,
        label: framework.shortname || framework.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [frameworks]);

  useEffect(() => {
    if (!isOpen) return;

    const firstFramework = frameworks[0];
    setSelectedFrameworkId(firstFramework?.id || '');
    setFrameworkName('');
    setShortName('');
    setDescription('');
    setAuthor(null);
    setError('');
  }, [isOpen, frameworks]);

  useEffect(() => {
    if (!selectedFrameworkId) return;

    const framework = metadataData?.controlFrameworks?.[0];
    if (!framework) return;
    if (framework.id !== selectedFrameworkId) return;

    setFrameworkName(framework.name || '');
    setShortName(framework.shortname || '');
    setDescription(framework.description || '');

    const existingAuthor = (framework.author || '').trim().toLowerCase();
    const matchedAuthor = authorOptions.find((option) => option.label.trim().toLowerCase() === existingAuthor) || null;
    if (matchedAuthor) {
      setAuthor(matchedAuthor);
    } else if ((framework.author || '').trim()) {
      setAuthor({
        value: framework.author!.trim(),
        label: framework.author!.trim(),
        profilePhotoPath: null,
      });
    } else {
      setAuthor(null);
    }
  }, [selectedFrameworkId, metadataData, authorOptions]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const frameworkSelectValue = frameworkOptions.find((option) => option.value === selectedFrameworkId) || null;
  const canSave = !!selectedFrameworkId && !!frameworkName.trim() && !isSaving && !isMetadataLoading;

  const onSave = async () => {
    if (!selectedFrameworkId || !frameworkName.trim()) return;

    setError('');

    try {
      await runUpdateMetadata({
        variables: {
          frameworkId: selectedFrameworkId,
          framework: {
            name: frameworkName.trim(),
            shortname: shortName.trim() || null,
            description: description.trim() || null,
            author: author?.label || null,
          },
        },
      });

      await onEdited();
      showSuccessToast({ title: 'Control framework updated' });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Control framework update failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSaving}>
      <Content>
        <Poppins className="m-title" px={32}>
          Edit control framework
        </Poppins>

        <Spacer $px={18} />
        <Panel>
          <InputLabel>CONTROL FRAMEWORK</InputLabel>
          <Spacer $px={8} />
          <FormSelect
            options={frameworkOptions}
            value={frameworkSelectValue}
            onChange={(nextValue: unknown) =>
              setSelectedFrameworkId(((nextValue as FrameworkOption | null)?.value || '').trim())
            }
            placeholder="Select control framework"
            isDisabled={isSaving || !frameworkOptions.length}
          />

          <Spacer $px={16} />
          <InputLabel>NAME</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={frameworkName}
            onChange={(e) => setFrameworkName(e.target.value)}
            placeholder="Enter control framework name"
            maxLength={255}
            disabled={!selectedFrameworkId || isSaving || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>SHORT NAME</InputLabel>
          <Spacer $px={8} />
          <FormInput
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="Enter short name"
            maxLength={255}
            disabled={!selectedFrameworkId || isSaving || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>DESCRIPTION</InputLabel>
          <Spacer $px={8} />
          <FormTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this control framework"
            maxLength={3000}
            disabled={!selectedFrameworkId || isSaving || isMetadataLoading}
          />

          <Spacer $px={16} />
          <InputLabel>AUTHOR</InputLabel>
          <Spacer $px={8} />
          <FormSelect
            placeholder="Select author"
            options={authorOptions}
            value={author}
            onChange={(nextValue: unknown) => setAuthor((nextValue as AuthorOption | null) || null)}
            isClearable
            isDisabled={!selectedFrameworkId || isSaving || isMetadataLoading}
          />
        </Panel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        {!frameworkOptions.length && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              No control frameworks available to edit.
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          <Button primary style={{ minWidth: 300 }} onClick={onSave} disabled={!canSave} loading={isSaving}>
            Save control framework
          </Button>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
